import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";

import FormModal from "../../component/common/FormModal";
import ActionButton from "../../component/common/ActionButton";
import LoadingDots from "../../component/common/LoadingDots";
import { ds } from "../../styles/designSystem";
import { AnonAvatar, AuthorLine, StatusChip, StatusSelect } from "./FeedbackBits";
import AttachmentPreviewList from "../../component/common/AttachmentPreviewList";
import { feedbackApi } from "./api/feedbackApi";
import { notify } from "../../util/toast";
import { confirm } from "../../util/confirmDialog";

const C = ds.colors;

/** 개별 댓글 행 — 익명 표시 + 본인/관리자 수정·삭제. */
function CommentRow({ comment, isAdmin, onChanged }) {
    const [menuEl, setMenuEl] = useState(null);
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState(comment.content);
    const [busy, setBusy] = useState(false);
    const canDelete = comment.mine || isAdmin;

    const saveEdit = async () => {
        if (!text.trim()) { notify("댓글 내용을 입력해주세요.", "warning"); return; }
        setBusy(true);
        try {
            await feedbackApi.updateComment(comment.id, { content: text.trim() });
            setEditing(false);
            onChanged?.();
        } catch (e) {
            notify(e?.message || "수정에 실패했습니다.", "error");
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        if (!(await confirm({ message: "댓글을 삭제할까요?", variant: "danger", confirmLabel: "삭제" }))) return;
        try {
            await feedbackApi.deleteComment(comment.id);
            onChanged?.();
        } catch (e) {
            notify(e?.message || "삭제에 실패했습니다.", "error");
        }
    };

    return (
        <Stack direction="row" spacing={1} sx={{ py: 1.25 }}>
            <AnonAvatar author={comment.author} size={30} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <AuthorLine
                            author={comment.author}
                            mine={comment.mine}
                            createdAt={comment.createdAt}
                            updatedAt={comment.updatedAt}
                            mineLabel="내 댓글"
                        />
                    </Box>
                    {(comment.mine || canDelete) && !editing && (
                        <>
                            <IconButton size="small" onClick={(e) => setMenuEl(e.currentTarget)}>
                                <MoreHorizRoundedIcon sx={{ fontSize: 18, color: C.textMuted }} />
                            </IconButton>
                            <Menu anchorEl={menuEl} open={Boolean(menuEl)} onClose={() => setMenuEl(null)}>
                                {comment.mine && (
                                    <MenuItem
                                        onClick={() => { setMenuEl(null); setText(comment.content); setEditing(true); }}
                                        sx={{ fontSize: 13 }}
                                    >
                                        수정
                                    </MenuItem>
                                )}
                                {canDelete && (
                                    <MenuItem
                                        onClick={() => { setMenuEl(null); remove(); }}
                                        sx={{ fontSize: 13, color: C.error }}
                                    >
                                        삭제
                                    </MenuItem>
                                )}
                            </Menu>
                        </>
                    )}
                </Stack>

                {editing ? (
                    <Box sx={{ mt: 0.5 }}>
                        <TextField
                            fullWidth
                            multiline
                            size="small"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            autoFocus
                        />
                        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 0.75 }}>
                            <ActionButton variant="cancel" label="취소" onClick={() => setEditing(false)} disabled={busy} />
                            <ActionButton variant="save" label="저장" onClick={saveEdit} loading={busy} />
                        </Stack>
                    </Box>
                ) : (
                    <Typography
                        sx={{
                            mt: 0.25,
                            fontSize: 13.5,
                            lineHeight: 1.6,
                            color: "#334155",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                        }}
                    >
                        {comment.content}
                    </Typography>
                )}
            </Box>
        </Stack>
    );
}

/**
 * 피드백 상세 모달 — 본문 + 상태 + 댓글 스레드.
 * 자체적으로 단건 재조회하여 최신 상태/댓글을 반영하고, 변경 시 onChanged 로 목록 갱신을 알린다.
 */
export default function FeedbackDetailModal({
    open,
    feedbackId,
    initialItem,
    isAdmin,
    onClose,
    onChanged,     // 목록 새로고침 (silentRefresh)
    onEdit,        // 본인 글 수정 → 부모가 작성 모달 오픈
}) {
    const [item, setItem] = useState(initialItem || null);
    const [loading, setLoading] = useState(!initialItem);
    const [commentText, setCommentText] = useState("");
    const [posting, setPosting] = useState(false);
    const [menuEl, setMenuEl] = useState(null);

    const refetch = useCallback(async (silent = false) => {
        if (!feedbackId) return;
        if (!silent) setLoading(true);
        try {
            const data = await feedbackApi.get(feedbackId);
            setItem(data?.data ?? data);
        } catch (e) {
            notify(e?.message || "피드백을 불러오지 못했습니다.", "warning");
        } finally {
            setLoading(false);
        }
    }, [feedbackId]);

    useEffect(() => {
        if (open) {
            setItem(initialItem || null);
            setCommentText("");
            setLoading(!initialItem);           // 시드가 있으면 즉시 표시(스피너 skip)
            refetch(Boolean(initialItem));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, feedbackId]);

    // 댓글/상태 변경 후: 상세 재조회 + 목록 갱신
    const handleLocalChange = async () => {
        await refetch(true);
        onChanged?.();
    };

    const submitComment = async () => {
        if (!commentText.trim()) return;
        setPosting(true);
        try {
            await feedbackApi.createComment(feedbackId, { content: commentText.trim() });
            setCommentText("");
            await handleLocalChange();
        } catch (e) {
            notify(e?.message || "댓글 등록에 실패했습니다.", "error");
        } finally {
            setPosting(false);
        }
    };

    const changeStatus = async (next) => {
        if (!item || next === item.status) return;
        if (next === "DONE") {
            const ok = await confirm({
                title: "완료 처리",
                message: "완료로 변경하면 작성자에게 완료 메일이 발송됩니다.\n진행할까요?",
            });
            if (!ok) return;
        }
        try {
            await feedbackApi.updateStatus(item.id, next);
            notify("상태가 변경되었습니다.", "success");
            await handleLocalChange();
        } catch (e) {
            notify(e?.message || "상태 변경에 실패했습니다.", "error");
        }
    };

    const removePost = async () => {
        if (!(await confirm({ message: "이 피드백을 삭제할까요?", variant: "danger", confirmLabel: "삭제" }))) return;
        try {
            await feedbackApi.remove(item.id);
            notify("삭제되었습니다.", "success");
            onChanged?.();
            onClose?.();
        } catch (e) {
            notify(e?.message || "삭제에 실패했습니다.", "error");
        }
    };

    const toggleLike = async () => {
        if (!item) return;
        setItem((p) => ({ ...p, likedByMe: !p.likedByMe, likeCount: Math.max(0, (p.likeCount ?? 0) + (p.likedByMe ? -1 : 1)) }));
        try {
            const res = await feedbackApi.toggleLike(item.id);
            const d = res?.data ?? res;
            setItem((p) => ({ ...p, likedByMe: d.liked, likeCount: d.likeCount }));
            onChanged?.();
        } catch (e) {
            notify(e?.message || "좋아요 처리에 실패했습니다.", "error");
            refetch(true);
        }
    };

    const comments = item?.comments || [];

    return (
        <FormModal
            open={open}
            onClose={onClose}
            title="피드백"
            subtitle="작성자가 익명으로 표시됩니다"
            maxWidth="sm"
            cancelLabel="닫기"
        >
            {loading || !item ? (
                <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
                    <LoadingDots label="불러오는 중" />
                </Box>
            ) : (
                <Box>
                    {/* 본문 헤더 */}
                    <Stack direction="row" spacing={1.25}>
                        <AnonAvatar author={item.author} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <AuthorLine
                                        author={item.author}
                                        mine={item.mine}
                                        createdAt={item.createdAt}
                                        updatedAt={item.updatedAt}
                                    />
                                </Box>
                                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
                                    {isAdmin ? (
                                        <StatusSelect value={item.status} onChange={changeStatus} />
                                    ) : (
                                        <StatusChip status={item.status} />
                                    )}
                                    {(item.mine || isAdmin) && (
                                        <>
                                            <IconButton size="small" onClick={(e) => setMenuEl(e.currentTarget)}>
                                                <MoreHorizRoundedIcon sx={{ fontSize: 20, color: C.textMuted }} />
                                            </IconButton>
                                            <Menu anchorEl={menuEl} open={Boolean(menuEl)} onClose={() => setMenuEl(null)}>
                                                {item.mine && (
                                                    <MenuItem
                                                        onClick={() => { setMenuEl(null); onEdit?.(item); }}
                                                        sx={{ fontSize: 13 }}
                                                    >
                                                        수정
                                                    </MenuItem>
                                                )}
                                                <MenuItem
                                                    onClick={() => { setMenuEl(null); removePost(); }}
                                                    sx={{ fontSize: 13, color: C.error }}
                                                >
                                                    {item.mine ? "삭제" : "삭제"}
                                                </MenuItem>
                                            </Menu>
                                        </>
                                    )}
                                </Stack>
                            </Stack>

                            {item.title && (
                                <Typography sx={{ mt: 1, fontSize: 16, fontWeight: 700, color: C.textPrimary }}>
                                    {item.title}
                                </Typography>
                            )}
                            <Typography
                                sx={{
                                    mt: 0.75,
                                    fontSize: 14,
                                    lineHeight: 1.75,
                                    color: "#1e293b",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                }}
                            >
                                {item.content}
                            </Typography>
                            {item.attachments?.length > 0 && (
                                <AttachmentPreviewList attachments={item.attachments}
                                    previewFn={feedbackApi.previewAttachment} sx={{ mt: 1.5 }} />
                            )}
                        </Box>
                    </Stack>

                    {/* 좋아요 */}
                    <Stack direction="row" alignItems="center" sx={{ mt: 1.5 }}>
                        <Box component="span" onClick={toggleLike}
                            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, cursor: "pointer",
                                px: 1.2, py: 0.5, borderRadius: 1,
                                border: `1px solid ${item.likedByMe ? C.error : C.border}`,
                                color: item.likedByMe ? C.error : C.textSecondary, "&:hover": { bgcolor: C.rowHover } }}
                        >
                            {item.likedByMe ? <FavoriteRoundedIcon sx={{ fontSize: 16 }} /> : <FavoriteBorderRoundedIcon sx={{ fontSize: 16 }} />}
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "inherit" }}>좋아요 {item.likeCount ?? 0}</Typography>
                        </Box>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    {/* 댓글 작성 */}
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.textSecondary, mb: 1 }}>
                        댓글 {comments.length}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ mb: 1 }}>
                        <TextField
                            fullWidth
                            size="small"
                            multiline
                            maxRows={4}
                            placeholder="댓글을 입력하세요 (익명)"
                            value={commentText}
                            onChange={(e) => setCommentText(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submitComment();
                            }}
                        />
                        <ActionButton
                            variant="primary"
                            label="등록"
                            onClick={submitComment}
                            loading={posting}
                            disabled={!commentText.trim()}
                            sx={{ flexShrink: 0 }}
                        />
                    </Stack>

                    {/* 댓글 목록 */}
                    <Box>
                        {comments.length === 0 ? (
                            <Typography sx={{ fontSize: 13, color: C.textMuted, py: 2, textAlign: "center" }}>
                                아직 댓글이 없습니다.
                            </Typography>
                        ) : (
                            comments.map((c, idx) => (
                                <Box
                                    key={c.id}
                                    sx={{ borderTop: idx === 0 ? "none" : `1px solid ${C.borderLight}` }}
                                >
                                    <CommentRow comment={c} isAdmin={isAdmin} onChanged={handleLocalChange} />
                                </Box>
                            ))
                        )}
                    </Box>
                </Box>
            )}
        </FormModal>
    );
}
